import {
  Box,
  Card,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Typography,
  type SelectChangeEvent,
  type SxProps,
  type Theme,
} from "~/common/mui";
import { ControlField } from "./ControlField";
import { ControlOptionLabel } from "./ControlOptionLabel";
import type { StaticImageSelectControlViewModel } from "./controlViewModel";

export interface StaticImageSelectControlProps
  extends Omit<StaticImageSelectControlViewModel, "type" | "controlIndex"> {
  onChange: (value: string) => void;
  className?: string;
  sx?: SxProps<Theme>;
}

export const StaticImageSelectControl = ({
  label,
  hideLabel,
  info,
  helperText,
  error,
  errorText,
  disabled = false,
  value,
  options,
  imageUrl,
  imageAlt,
  placeholder,
  standardLabel,
  onChange,
  className,
  sx,
}: StaticImageSelectControlProps) => {
  const selectedOption = options.find((option) => option.value === value);
  const handleChange = (event: SelectChangeEvent<unknown>) => onChange(event.target.value as string);

  return (
    <Card variant="outlined" className={className} sx={{ borderRadius: 2, boxShadow: "none", ...sx }}>
      <Stack gap={2} p={2}>
        {imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            alt={imageAlt ?? label}
            sx={{ width: "100%", aspectRatio: "529 / 225", objectFit: "contain" }}
          />
        ) : null}
        <ControlField
          label={label}
          hideLabel={hideLabel}
          info={info}
          helperText={helperText}
          error={Boolean(error)}
          errorText={errorText}
        >
          <FormControl fullWidth error={Boolean(error)} disabled={disabled}>
            <Select
              value={value ?? ""}
              displayEmpty
              inputProps={{ "aria-label": label }}
              sx={(theme) => ({
                backgroundColor: theme.palette.surfaceContainer.lowest,
                "& .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.divider },
              })}
              renderValue={() =>
                selectedOption ? (
                  <ControlOptionLabel option={selectedOption} standardLabel={standardLabel} />
                ) : (
                  <Typography variant="bodyLarge" color="textSecondary">
                    {placeholder}
                  </Typography>
                )
              }
              onChange={handleChange}
            >
              {options.map((option) => (
                <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
                  <ControlOptionLabel option={option} standardLabel={standardLabel} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </ControlField>
      </Stack>
    </Card>
  );
};
